// ./src/components/features/Reminders/reminderDisplay.ts
//
// Shared display + form helpers for the Reminders feature — human labels for the enum-like fields, a
// one-line schedule summary, and the offset⇄minutes conversion the edit drawer uses (the API stores a
// single `offsetMinutes`; the UI edits it as a value + unit).
import { clockFormat, formatDateTime } from "@em/utils";

import { Reminder, REMINDER_KIND, REMINDER_CHANNEL, REMINDER_SCHEDULE_KIND } from "@/utils/api";

// Month-name date + a LOCALE-AWARE clock (12h "h:mm A" vs 24h "HH:mm"), so the list matches the picker and
// the viewer's regional time format instead of a hardcoded AM/PM.
const DATETIME_FMT = `MMM D, YYYY · ${clockFormat() === "24h" ? "HH:mm" : "h:mm A"}`;

// ⚠ A reminder's time is shown on the EVENT's clock (ROADMAP #93) — the zone it is picked in. Null → this device's.
const atLabel = (iso: string, tz: string | null): string => formatDateTime(iso, DATETIME_FMT, tz || undefined);

export const kindLabel = (kind?: string | null): string => {
  switch (kind) {
    case REMINDER_KIND.EVENT_APPROACHING:
      return "Event approaching";
    case REMINDER_KIND.RSVP_NO_RESPONSE:
      return "RSVP nudge";
    default:
      return "Reminder";
  }
};

export const kindColor = (kind?: string | null): string =>
  kind === REMINDER_KIND.RSVP_NO_RESPONSE ? "grape" : "blue";

export const channelLabel = (channel?: string | null): string => {
  switch (channel) {
    case REMINDER_CHANNEL.SMS:
      return "SMS";
    case REMINDER_CHANNEL.EMAIL:
    default:
      return "Email";
  }
};

// ── Offset ⇄ minutes (before-event schedule) ────────────────────────────────
export type OffsetUnit = "minutes" | "hours" | "days";

const UNIT_MINUTES: Record<OffsetUnit, number> = { minutes: 1, hours: 60, days: 1440 };

/** Split a minutes value into the largest whole unit that divides it evenly (defaults to 1 day). */
export const offsetToParts = (minutes?: number | null): { value: number; unit: OffsetUnit } => {
  if (minutes == null || minutes <= 0) {
    return { value: 1, unit: "days" };
  }

  if (minutes % 1440 === 0) {
    return { value: minutes / 1440, unit: "days" };
  }

  if (minutes % 60 === 0) {
    return { value: minutes / 60, unit: "hours" };
  }

  return { value: minutes, unit: "minutes" };
};

export const partsToOffset = (value: number, unit: OffsetUnit): number => Math.round(value) * UNIT_MINUTES[unit];

/** "1 day", "3 hours", "45 minutes" — the human form of an offset. */
export const offsetPhrase = (minutes?: number | null): string => {
  const { value, unit } = offsetToParts(minutes);
  const singular = value === 1;
  const noun = unit === "days" ? "day" : unit === "hours" ? "hour" : "minute";

  return `${value} ${noun}${singular ? "" : "s"}`;
};

/** A one-line description of WHEN a reminder fires (and whether it repeats) — on the event's clock (`tz`). */
export const scheduleSummary = (reminder: Reminder, tz: string | null): string => {
  const repeat = reminder.repeatEveryDays ? ` · every ${reminder.repeatEveryDays} day${reminder.repeatEveryDays === 1 ? "" : "s"}` : "";

  if (reminder.scheduleKind === REMINDER_SCHEDULE_KIND.ABSOLUTE) {
    const when = reminder.sendAt ? atLabel(reminder.sendAt, tz) : "no date set";

    return `${when}${repeat}`;
  }

  return `${offsetPhrase(reminder.offsetMinutes)} before the event${repeat}`;
};

/** When the reminder next fires (its materialized send_at) as a friendly absolute date, or "—". */
export const nextSendLabel = (reminder: Reminder, tz: string | null): string =>
  reminder.sendAt ? atLabel(reminder.sendAt, tz) : "—";

export const lastRunLabel = (reminder: Reminder, tz: string | null): string =>
  reminder.lastRunAt ? atLabel(reminder.lastRunAt, tz) : "Never";
